import { formatShortDate } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lightbulb, Lock, Trophy, Zap } from 'lucide-react';
import { useMemo } from 'react';

import { useTeamDetailContext } from './teamDetailContext';

interface ChallengeWithStatus {
  id: string;
  name: string;
  status: 'locked' | 'available' | 'completed';
  points: number;
  earnedPoints: number | null;
  completedAt: string | null;
  /** DT-157+. Activation gate metadata. Drives the Force-activate button. */
  activationMode?: 'available' | 'manual' | 'gps_proximity' | 'qr_code' | 'ibeacon';
  requiresActivation?: boolean;
  isActivated?: boolean;
}

interface ChallengeGroup {
  id: string;
  name: string;
  status: 'locked' | 'available' | 'completed';
  progress: { completed: number; total: number };
  challenges: ChallengeWithStatus[];
}

interface GroupedChallengesResponse {
  groups: ChallengeGroup[];
  ungroupedChallenges: ChallengeWithStatus[];
}

interface EventGroupMeta {
  id: string;
  color?: string | null;
  unlockMode: 'sequential' | 'parallel';
}

// Same color palette EventGroupCard uses on the event Challenges tab —
// keeps the team-progress view chromatically aligned with what an
// admin sees on the matching event tab.
const COLOR_CLASSES: Record<string, { bg: string; border: string; text: string }> = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  pink: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700' },
};

const NEUTRAL_COLOR = COLOR_CLASSES.blue;

/**
 * Per-group breakdown of this team's challenge progress. Visually
 * mirrors the event Challenges tab — color-banded bordered group
 * cards w/ sequential numbering — but oriented around team progress
 * rather than admin actions: each row shows a status icon (locked /
 * available / completed) and either the points awarded or the
 * potential payout.
 *
 * We merge the team's grouped challenges (which carry status) with
 * the event groups list (which carries `color` + `unlockMode`) so
 * the chrome matches what the admin set up on the event side.
 */
export function EventTeamChallengesTab() {
  const { eventId, teamId } = useTeamDetailContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const teamChallengesQuery = useQuery<{
    success: boolean;
    data: GroupedChallengesResponse;
  }>({
    queryKey: ['EventTeamChallenges', eventId, teamId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams/${teamId}/challenges`),
    enabled: !!eventId && !!teamId,
  });

  // DT-186. Force-activate bypasses whatever activation gate the
  // challenge has. Used when a team is stuck (basement GPS, dead
  // beacon, wet QR) and a facilitator needs to unblock them.
  const forceActivateMutation = useMutation({
    mutationFn: async (challengeId: string) =>
      apiClient.post(
        `/events/${eventId}/teams/${teamId}/challenges/${challengeId}/force-activate`,
      ),
    onSuccess: () => {
      toast.success('Challenge activated for this team');
      queryClient.invalidateQueries({
        queryKey: ['EventTeamChallenges', eventId, teamId],
      });
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to activate challenge');
    },
  });

  const eventGroupsQuery = useQuery<{ success: boolean; data: EventGroupMeta[] }>({
    queryKey: ['EventGroupMeta', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/groups`),
    enabled: !!eventId,
    staleTime: 60_000,
  });

  const groupMetaById = useMemo(() => {
    const map = new Map<string, EventGroupMeta>();
    eventGroupsQuery.data?.data?.forEach((g) => map.set(g.id, g));
    return map;
  }, [eventGroupsQuery.data]);

  const isLoading = teamChallengesQuery.isLoading || eventGroupsQuery.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-48" />
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  const payload = teamChallengesQuery.data?.data;
  const groups = payload?.groups ?? [];
  const ungrouped = payload?.ungroupedChallenges ?? [];
  const totalChallenges =
    groups.reduce((acc, g) => acc + g.challenges.length, 0) + ungrouped.length;
  const completedCount =
    groups.reduce((acc, g) => acc + g.progress.completed, 0) +
    ungrouped.filter((c) => c.status === 'completed').length;
  const allEmpty = totalChallenges === 0;

  if (allEmpty) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardContent className="px-4 py-12 text-center">
          <Lightbulb className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
          <h3 className="mb-1 text-lg font-medium">No challenges yet</h3>
          <p className="text-sm text-muted-foreground">
            This event has no challenges configured.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header echoes the event Challenges tab — Lightbulb icon,
          title, and a count description in the same layout. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Challenges</h2>
          <span className="text-sm text-muted-foreground">
            ({completedCount} / {totalChallenges} completed
            {groups.length > 0
              ? `, ${groups.length} group${groups.length === 1 ? '' : 's'}`
              : ''}
            )
          </span>
        </div>
      </div>

      {groups.map((group) => (
        <TeamGroupCard
          key={group.id}
          group={group}
          meta={groupMetaById.get(group.id)}
          onForceActivate={(id) => forceActivateMutation.mutate(id)}
          forceActivatingId={
            forceActivateMutation.isPending
              ? (forceActivateMutation.variables as string | undefined)
              : undefined
          }
        />
      ))}

      {ungrouped.length > 0 && (
        <TeamGroupCard
          group={{
            id: '__ungrouped__',
            name: 'Other Challenges',
            status: 'available',
            progress: {
              completed: ungrouped.filter((c) => c.status === 'completed').length,
              total: ungrouped.length,
            },
            challenges: ungrouped,
          }}
          meta={undefined}
          onForceActivate={(id) => forceActivateMutation.mutate(id)}
          forceActivatingId={
            forceActivateMutation.isPending
              ? (forceActivateMutation.variables as string | undefined)
              : undefined
          }
        />
      )}
    </div>
  );
}

function TeamGroupCard({
  group,
  meta,
  onForceActivate,
  forceActivatingId,
}: {
  group: ChallengeGroup;
  meta: EventGroupMeta | undefined;
  onForceActivate: (challengeId: string) => void;
  forceActivatingId?: string;
}) {
  const colorClasses = COLOR_CLASSES[meta?.color || 'blue'] || NEUTRAL_COLOR;
  const isLocked = group.status === 'locked';
  const isSequential = meta?.unlockMode === 'sequential';

  return (
    <Card className={cn('rounded-xl border shadow-sm !py-0 !gap-0', colorClasses.border)}>
      <CardHeader
        className={cn(
          'px-4 py-3 border-b flex flex-row items-center justify-between rounded-t-xl',
          colorClasses.bg,
          colorClasses.border,
        )}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isLocked ? (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/80">
              <Lock className="h-4 w-4 text-muted-foreground" />
            </span>
          ) : (
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/80',
                colorClasses.text,
              )}>
              <Trophy className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className={cn('font-semibold truncate', colorClasses.text)}>
                {group.name}
              </h3>
              {meta && (
                <span className="text-xs bg-white/80 px-2 py-0.5 rounded-full text-muted-foreground shrink-0">
                  {isSequential ? 'Sequential' : 'All at once'}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {group.progress.completed} of {group.progress.total} completed
            </p>
          </div>
        </div>
        <span
          className={cn(
            'text-sm font-semibold rounded-full bg-white/80 px-2.5 py-0.5 shrink-0',
            colorClasses.text,
          )}>
          {group.progress.completed} / {group.progress.total}
        </span>
      </CardHeader>

      <CardContent className="p-0">
        {group.challenges.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No challenges in this group.
          </div>
        ) : (
          <ul className="divide-y">
            {group.challenges.map((challenge, index) => (
              <ChallengeRow
                key={challenge.id}
                challenge={challenge}
                index={index}
                showSequentialNumber={isSequential}
                onForceActivate={onForceActivate}
                isForceActivating={forceActivatingId === challenge.id}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ChallengeRow({
  challenge,
  index,
  showSequentialNumber,
  onForceActivate,
  isForceActivating,
}: {
  challenge: ChallengeWithStatus;
  index: number;
  showSequentialNumber: boolean;
  onForceActivate: (challengeId: string) => void;
  isForceActivating: boolean;
}) {
  // DT-186. Show the Force-activate affordance only while the gate
  // matters: not yet completed, and the activation gate hasn't been
  // cleared. Once the team activates (or completes) the row falls
  // back to its standard chrome.
  const showForceActivate =
    challenge.requiresActivation === true &&
    challenge.isActivated !== true &&
    challenge.status !== 'completed';

  return (
    <li className="flex items-center gap-3 p-3">
      <div className="flex items-center gap-2 text-muted-foreground shrink-0">
        {showSequentialNumber ? (
          <span className="text-sm font-medium w-5 text-center">{index + 1}</span>
        ) : (
          <span className="w-5" />
        )}
        <StatusIcon status={challenge.status} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{challenge.name}</p>
        {challenge.completedAt && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Completed {formatShortDate(challenge.completedAt)}
          </p>
        )}
        {showForceActivate && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Activation: {challenge.activationMode}
          </p>
        )}
      </div>
      {showForceActivate && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onForceActivate(challenge.id)}
          loading={isForceActivating}
          disabled={isForceActivating}
        >
          <Zap className="h-3.5 w-3.5" />
          Force activate
        </Button>
      )}
      <div className="shrink-0 text-right">
        {challenge.status === 'completed' ? (
          <p className="font-mono text-sm font-semibold tabular-nums text-success">
            +{challenge.earnedPoints ?? challenge.points}
          </p>
        ) : (
          <p className="font-mono text-sm tabular-nums text-muted-foreground">
            {challenge.points} pts
          </p>
        )}
      </div>
    </li>
  );
}

function StatusIcon({ status }: { status: ChallengeWithStatus['status'] }) {
  if (status === 'completed') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-success/10">
        <Check className="h-4 w-4 text-success" />
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted">
        <Lock className="h-4 w-4 text-muted-foreground" />
      </span>
    );
  }
  return <span className="h-7 w-7 rounded-full border-2 border-muted-foreground/30" />;
}
